import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { ChevronDown, ChevronRight, Loader2, RotateCcw, ShieldCheck } from 'lucide-react';

import { useConfiguredDashboard } from '@/hooks/useAnalytics';
import { useCampaigns } from '@/hooks/useCampaigns';
import { usePageTitle } from '@/hooks/usePageTitle';
import { type GlobalFilters } from '@/lib/api/analytics.api';
import { cn } from '@/lib/utils';

import { CallsPanel } from './dashboard/components/CallsPanel';
import { MindMapCanvas } from './dashboard/components/MindMapCanvas';
import { ACCENT } from './dashboard/components/MindMapNode';

import type {
  DashboardConfig,
  Direction,
  TreeNodeMetric,
  QualitySummary,
} from '@repo/shared-types';

// ─── Config-driven Mind Map ──────────────────────────────────────────────────
function ConfiguredMindMap({
  config,
  data,
  direction,
  selectedClassification,
  onClassificationSelect,
  onDirectionSelect,
  disableSplit,
}: {
  config: DashboardConfig;
  data: Record<string, TreeNodeMetric>;
  direction: Direction;
  selectedClassification: string | null;
  onClassificationSelect: (classification: string | null, metricKey?: string | null) => void;
  onDirectionSelect: (dir: Direction | null) => void;
  disableSplit?: boolean;
}) {
  return (
    <MindMapCanvas
      config={config}
      data={data}
      direction={direction}
      selectedClassification={selectedClassification}
      onClassificationSelect={onClassificationSelect}
      onDirectionSelect={onDirectionSelect}
      disableSplit={disableSplit}
    />
  );
}

// ─── Mobile stat card ────────────────────────────────────────────────────────
function MobileStatCard({
  label,
  count,
  pct,
  accent,
  classification,
  metricKey,
  onSelect,
  selected,
}: {
  label: string;
  count: number;
  pct?: string;
  accent: string;
  classification: string;
  metricKey?: string;
  onSelect: (c: string, metricKey?: string) => void;
  selected: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(classification, metricKey)}
      className={cn(
        'flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition-colors',
        selected ? 'border-primary/30 bg-muted' : 'border-border hover:bg-muted/50',
      )}
    >
      <div className="flex items-center gap-2.5">
        <div className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: accent }} />
        <span className="text-foreground text-sm font-medium">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        {pct && <span className="text-muted-foreground text-xs">{pct}</span>}
        <span className="text-sm font-bold tabular-nums" style={{ color: accent }}>
          {count.toLocaleString()}
        </span>
        <ChevronRight className="text-muted-foreground/50 h-3.5 w-3.5" />
      </div>
    </button>
  );
}

// ─── Collapsible section ─────────────────────────────────────────────────────
function MobileSection({
  title,
  accent,
  defaultOpen = false,
  children,
}: {
  title: string;
  accent: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="overflow-hidden rounded-2xl border">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="bg-muted/50 flex w-full items-center justify-between px-4 py-3"
      >
        <span className="text-xs font-bold tracking-widest uppercase" style={{ color: accent }}>
          {title}
        </span>
        <ChevronDown
          className={cn('text-muted-foreground h-4 w-4 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && <div className="flex flex-col gap-1.5 p-2">{children}</div>}
    </div>
  );
}

// ─── Config-driven mobile view ───────────────────────────────────────────────
function ConfiguredMobileView({
  config,
  data,
  selected,
  onSelect,
}: {
  config: DashboardConfig;
  data: Record<string, TreeNodeMetric>;
  selected: string | null;
  onSelect: (c: string, metricKey?: string) => void;
}) {
  const pctStr = (part: number, whole: number) =>
    whole > 0 ? `${Math.round((part / whole) * 100)}%` : undefined;

  // Group nodes by parent edges to create sections
  const rootNodes = config.tree.nodes.filter((n) => !n.parentMetricKey);
  const childMap = new Map<string, typeof config.tree.nodes>();
  for (const edge of config.tree.edges) {
    const children = childMap.get(edge.source) ?? [];
    const child = config.tree.nodes.find((n) => n.id === edge.target);
    if (child) children.push(child);
    childMap.set(edge.source, children);
  }

  function renderNodeAndChildren(nodeId: string, depth = 0): React.ReactNode {
    const nodeCfg = config.tree.nodes.find((n) => n.id === nodeId);
    if (!nodeCfg) return null;

    // Same single-element metricKeys fallback as ConfiguredDashboard.
    const effectiveMetricKey = nodeCfg.metricKey ?? nodeCfg.metricKeys?.[0];
    const metric = effectiveMetricKey ? (data[effectiveMetricKey] ?? { count: 0 }) : { count: 0 };
    const parentMetric = nodeCfg.parentMetricKey ? data[nodeCfg.parentMetricKey] : null;
    const accent = ACCENT[nodeCfg.colorClass] ?? '#64748b';
    const children = childMap.get(nodeId) ?? [];

    return (
      <div key={nodeId}>
        <MobileStatCard
          label={nodeCfg.label}
          count={metric.count}
          pct={parentMetric ? pctStr(metric.count, parentMetric.count) : undefined}
          accent={accent}
          classification={nodeCfg.classification}
          metricKey={effectiveMetricKey}
          onSelect={onSelect}
          selected={selected === nodeCfg.classification}
        />
        {/* Render sub-items */}
        {nodeCfg.subItems?.map((sub) => {
          const sm = data[sub.metricKey] ?? { count: 0 };
          const base = data[sub.pctBaseMetricKey] ?? { count: 0 };
          const subAccent = ACCENT[sub.colorClass] ?? '#64748b';
          return (
            <MobileStatCard
              key={sub.metricKey}
              label={sub.label}
              count={sm.count}
              pct={pctStr(sm.count, base.count)}
              accent={subAccent}
              classification={sub.classification}
              metricKey={sub.metricKey}
              onSelect={onSelect}
              selected={selected === sub.classification}
            />
          );
        })}
        {children.map((child) => renderNodeAndChildren(child.id, depth + 1))}
      </div>
    );
  }

  // Build sections from root nodes and their immediate children
  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto p-4">
      {rootNodes.map((root) => {
        const accent = ACCENT[root.colorClass] ?? '#64748b';
        return (
          <MobileSection key={root.id} title={root.label} accent={accent} defaultOpen>
            {renderNodeAndChildren(root.id)}
          </MobileSection>
        );
      })}
    </div>
  );
}

// ─── Quality summary widget ──────────────────────────────────────────────────
function QualitySummaryWidget({ summary }: { summary: QualitySummary }) {
  const [expanded, setExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  const hasDetails =
    summary.goodCallRate !== null ||
    summary.avgTechnicalScore !== null ||
    summary.avgConversationScore !== null ||
    summary.avgTaskCompletionScore !== null ||
    summary.avgPromptAdherenceScore !== null;

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [summary, expanded]);

  return (
    <div
      className={cn(
        'bg-background/95 absolute bottom-5 left-5 z-10 rounded-2xl border shadow-2xl backdrop-blur-md transition-all duration-300 ease-in-out',
        expanded ? 'w-72' : 'w-64',
      )}
    >
      {/* Header */}
      <div
        role={hasDetails ? 'button' : undefined}
        tabIndex={hasDetails ? 0 : undefined}
        onClick={hasDetails ? () => setExpanded((e) => !e) : undefined}
        className={cn(
          'flex w-full items-center justify-between px-4 pt-4 pb-2',
          hasDetails && 'cursor-pointer',
        )}
      >
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
          <span className="text-muted-foreground text-[11px] font-bold tracking-widest uppercase">
            Quality Overview
          </span>
        </div>
        {hasDetails && (
          <ChevronDown
            className={cn(
              'text-muted-foreground h-3.5 w-3.5 transition-transform duration-300',
              expanded && 'rotate-180',
            )}
          />
        )}
      </div>

      {/* Collapsed stats — always visible */}
      <div className="space-y-2 px-4 pb-1">
        <StatRow
          label="Total Reviewed"
          value={summary.totalReviewed.toLocaleString()}
          accent="var(--foreground)"
        />
        {summary.avgQualityScore !== null && (
          <StatRow label="Avg Quality" value={`${summary.avgQualityScore}/10`} accent="#3b82f6" />
        )}
      </div>

      {/* Expandable detail section */}
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: expanded ? `${contentHeight}px` : '0px', opacity: expanded ? 1 : 0 }}
      >
        <div ref={contentRef} className="space-y-2 px-4 pt-1 pb-4">
          <div className="bg-border my-2 h-px" />
          {summary.goodCallRate !== null && (
            <StatRow label="Good Call Rate" value={`${summary.goodCallRate}%`} accent="#10b981" />
          )}
          {summary.avgTechnicalScore !== null && (
            <StatRow label="Technical" value={`${summary.avgTechnicalScore}/10`} accent="#f97316" />
          )}
          {summary.avgConversationScore !== null && (
            <StatRow
              label="Conversation"
              value={`${summary.avgConversationScore}/10`}
              accent="#8b5cf6"
            />
          )}
          {summary.avgTaskCompletionScore !== null && (
            <StatRow
              label="Task Completion"
              value={`${summary.avgTaskCompletionScore}/10`}
              accent="#f59e0b"
            />
          )}
          {summary.avgPromptAdherenceScore !== null && (
            <StatRow
              label="Prompt Adherence"
              value={`${summary.avgPromptAdherenceScore}/10`}
              accent="#14b8a6"
            />
          )}
        </div>
      </div>

      {/* Bottom padding when collapsed */}
      {!expanded && <div className="h-2" />}
    </div>
  );
}

function Divider() {
  return <div className="bg-border h-4 w-px shrink-0" />;
}

function StatRow({ label, value, accent }: { label: string; value: string; accent: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground text-[11px]">{label}</span>
      <span className="text-[13px] font-bold tabular-nums" style={{ color: accent }}>
        {value}
      </span>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function findLabelForClassification(
  config: DashboardConfig,
  classification: string,
): string | undefined {
  for (const node of config.tree.nodes) {
    if (node.classification === classification) return node.label;
    for (const sub of node.subItems ?? []) {
      if (sub.classification === classification) return sub.label;
    }
  }
  return undefined;
}

// ─── Main page ───────────────────────────────────────────────────────────────
export const CallQualityDashboardPage = () => {
  usePageTitle('Call Quality');
  const [executedAtFrom, setExecutedAtFrom] = useState('');
  const [executedAtTo, setExecutedAtTo] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [selectedClassification, setSelectedClassification] = useState<string | null>(null);
  const [selectedMetricKey, setSelectedMetricKey] = useState<string | null>(null);

  // v2 path: store the metricKey from the clicked node directly — the
  // lookup-by-classification is ambiguous when multiple nodes share the same
  // value (e.g. `all`).
  const handleSelect = useCallback((classification: string | null, metricKey?: string | null) => {
    setSelectedClassification(classification);
    setSelectedMetricKey(metricKey ?? null);
  }, []);

  const { data: campaignsData } = useCampaigns({ limit: 100 });
  const campaigns = campaignsData?.data ?? [];

  const globalFilters: GlobalFilters = useMemo(
    () => ({
      direction: 'ALL' as const,
      ...(executedAtFrom ? { executedAtFrom } : {}),
      ...(executedAtTo ? { executedAtTo } : {}),
      ...(campaignId ? { campaignId } : {}),
      timePreset: 'all',
      type: 'quality' as const,
    }),
    [executedAtFrom, executedAtTo, campaignId],
  );

  const { data: response, isLoading, error } = useConfiguredDashboard(globalFilters);

  if (isLoading && !response) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error && !response) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-destructive text-sm">Failed to load dashboard data.</p>
      </div>
    );
  }

  if (!response) return null;

  const { config, data, summary } = response;

  return (
    <ReactFlowProvider>
      <div className="relative -m-8 flex h-screen flex-col overflow-hidden">
        {/* ── Toolbar ─────────────────────────────────────────────────── */}
        <div className="bg-background/80 flex shrink-0 flex-wrap items-center gap-2 border-b px-4 py-2 backdrop-blur-md">
          <span className="text-foreground text-sm font-semibold tracking-tight">Call Quality</span>

          <div className="hidden items-center gap-2 sm:flex">
            <Divider />
            <label className="text-muted-foreground text-xs font-medium">From</label>
            <input
              type="date"
              value={executedAtFrom}
              onChange={(e) => setExecutedAtFrom(e.target.value)}
              className="focus:ring-primary bg-background text-foreground h-7 rounded-md border px-2 text-xs focus:ring-1 focus:outline-none dark:border-slate-700"
            />
            <span className="text-muted-foreground text-xs">→</span>
            <input
              type="date"
              value={executedAtTo}
              onChange={(e) => setExecutedAtTo(e.target.value)}
              className="focus:ring-primary bg-background text-foreground h-7 rounded-md border px-2 text-xs focus:ring-1 focus:outline-none dark:border-slate-700"
            />
          </div>

          {campaigns.length > 0 && (
            <div className="hidden items-center gap-2 sm:flex">
              <Divider />
              <label className="text-muted-foreground text-xs font-medium">Campaign</label>
              <select
                value={campaignId}
                onChange={(e) => setCampaignId(e.target.value)}
                className="focus:ring-primary bg-background text-foreground h-7 max-w-45 rounded-md border px-2 text-xs focus:ring-1 focus:outline-none dark:border-slate-700"
              >
                <option value="">All campaigns</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <Divider />

          <button
            type="button"
            title="Reset all filters"
            onClick={() => {
              setExecutedAtFrom('');
              setExecutedAtTo('');
              setCampaignId('');
              setSelectedClassification(null);
              setSelectedMetricKey(null);
            }}
            className="text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-colors"
          >
            <RotateCcw className="h-3 w-3" />
            Reset
          </button>
        </div>

        {/* ── Canvas — desktop only ─────────────────────────────────── */}
        <div className="relative hidden min-h-0 flex-1 flex-row overflow-hidden md:flex">
          <div className="relative min-h-0 flex-1">
            <ConfiguredMindMap
              config={config}
              data={data}
              direction="ALL"
              selectedClassification={selectedClassification}
              onClassificationSelect={handleSelect}
              onDirectionSelect={() => {}}
              disableSplit
            />
            {summary && <QualitySummaryWidget summary={summary} />}
          </div>
        </div>

        {/* ── Mobile list — visible below md ────────────────────────── */}
        <div className="relative min-h-0 flex-1 overflow-hidden md:hidden">
          <ConfiguredMobileView
            config={config}
            data={data}
            selected={selectedClassification}
            onSelect={handleSelect}
          />
        </div>

        {/* ── Drill-down panel ──────────────────────────────────────── */}
        <CallsPanel
          classification={selectedClassification as any}
          metricKey={selectedMetricKey ?? undefined}
          globalFilters={globalFilters}
          onClose={() => {
            setSelectedClassification(null);
            setSelectedMetricKey(null);
          }}
          classificationLabel={
            selectedClassification
              ? findLabelForClassification(config, selectedClassification)
              : undefined
          }
        />
      </div>
    </ReactFlowProvider>
  );
};
